import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { 
  Target,
  BookOpen,
  Users,
  Award,
  Building2,
  Lightbulb,
  Globe,
  CheckCircle,
  ArrowRight,
  Download,
  Shield,
  GraduationCap,
  AlertTriangle,
  Scale,
  Layers
} from 'lucide-react';

const cpmLevels = [
  {
    level: 1,
    title: "Fundamental Awareness",
    focus: "Ethics, foundational knowledge base, and professional orientation.",
    description: "This level establishes essential ethical understanding, scope of practice awareness, and theoretical grounding required for entry into structured medical education systems.",
    icon: BookOpen
  },
  {
    level: 2,
    title: "Clinical Understanding",
    focus: "Applied knowledge and early integration.",
    description: "This level defines the transition from foundational knowledge to contextual clinical interpretation within supervised academic environments.",
    icon: GraduationCap
  },
  {
    level: 3,
    title: "Supervised Practice",
    focus: "Hands-on experience under structured mentorship.",
    description: "This stage represents competency development within formally supervised training environments governed by institutional standards.",
    icon: Users
  },
  {
    level: 4,
    title: "Independent Clinical Application",
    focus: "Self-directed clinical decision-making within defined practice boundaries.",
    description: "This level reflects readiness for accountable, independent professional judgment under regulatory frameworks.",
    icon: Target
  },
  {
    level: 5,
    title: "System-Level Responsibility",
    focus: "Leadership, governance awareness, and quality assurance.",
    description: "This stage incorporates responsibility for clinical oversight, team coordination, and quality systems implementation.",
    icon: Building2
  },
  {
    level: 6,
    title: "Innovation & Research Integration",
    focus: "Integration of research, data science, and emerging technologies.",
    description: "This level reflects engagement with evidence-based innovation, research participation, and responsible adoption of advancing medical systems.",
    icon: Lightbulb
  },
  {
    level: 7,
    title: "Global Excellence & Influence",
    focus: "Policy engagement, advocacy, and transformative professional contribution.",
    description: "This level represents contribution to global standards, professional policy, institutional governance, and international collaboration.",
    icon: Globe
  }
];

const purposes = [
  "Provide structural guidance for curriculum development across medical disciplines",
  "Define progressive competency benchmarks",
  "Support objective examination blueprint design",
  "Align educational delivery with measurable performance standards",
  "Embed patient safety and ethical principles into program architecture",
  "Guide institutional competency mapping and governance structures"
];

const applicabilityAreas = [
  "Medical specialties",
  "Surgical specialties",
  "Diagnostic disciplines",
  "Clinical subspecialties",
  "Academic medical education structures"
];

const legalClarifications = [
  "Is a framework and guideline document",
  "Does not issue certification",
  "Does not grant professional titles",
  "Does not confer licensure",
  "Does not independently validate clinical competency"
];

export default function CPMPage() {
  const handleDownloadPDF = () => {
    window.open('https://customer-assets.emergentagent.com/job_32d6feb3-5f9e-4ec6-ac88-bba0f5d97d0d/artifacts/4lhsld4m_Clinical%20Progression%20Matrix%20%28CPM%29%20%283%29.pdf', '_blank');
  };

  return (
    <div className="bg-white min-h-screen" data-testid="cpm-page">
      {/* Hero Section */}
      <section className="py-16 border-b border-slate-100">
        <div className="imcac-container text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#1B2A4A]/5 rounded-sm mb-6">
            <Layers className="h-4 w-4 text-[#C5A572]" />
            <span className="text-xs font-semibold tracking-wider text-[#1B2A4A] uppercase">Competency Framework</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-[#1B2A4A] font-heading mb-4">
            Clinical Progression Matrix (CPM)
          </h1>
          <p className="text-lg text-[#1B2A4A] font-medium mb-6">
            A Structural Framework for Competency Architecture in Medical Education
          </p>
          
          <div className="flex flex-wrap justify-center gap-3">
            <Button 
              className="bg-[#1B2A4A] text-white hover:bg-[#142238] rounded-sm"
              onClick={() => window.location.href = '/examination-pathways'}
              data-testid="explore-programs-btn"
            >
              Explore IMCAC Programs <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button 
              variant="outline"
              className="border-[#C5A572] text-[#C5A572] hover:bg-[#C5A572] hover:text-white rounded-sm"
              onClick={handleDownloadPDF}
              data-testid="download-cpm-btn"
            >
              <Download className="h-4 w-4 mr-2" /> Download CPM Framework
            </Button>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-12">
        <div className="imcac-container max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-sm bg-[#1B2A4A] flex items-center justify-center">
              <BookOpen className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-[#1B2A4A] font-heading">Overview</h2>
          </div>
          
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              The Clinical Progression Matrix (CPM) is a structured professional framework designed to guide 
              the development of curricula, competency benchmarks, assessment models, and examination standards 
              across medical specialties and subspecialties.
            </p>
            
            <Card className="border-l-4 border-l-[#C5A572] bg-[#C5A572]/5">
              <CardContent className="p-4">
                <p className="text-[#1B2A4A] font-medium">
                  The CPM is not a course, training program, certification pathway, or qualification.
                </p>
              </CardContent>
            </Card>
            
            <p>
              It functions exclusively as a reference architecture to support educational institutions, 
              professional councils, accreditation bodies, and examination authorities in designing progressive, 
              competency-based development structures aligned with safety, ethics, and professional accountability.
            </p>
            <p>
              The framework defines stages of professional maturity and responsibility but does not independently 
              confer credentials, status, or licensure.
            </p>
          </div>
        </div>
      </section>

      <Separator className="max-w-4xl mx-auto" />

      {/* Purpose Section */}
      <section className="py-12">
        <div className="imcac-container max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-sm bg-[#1B2A4A] flex items-center justify-center">
              <Target className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-[#1B2A4A] font-heading">Purpose of the Clinical Progression Matrix</h2>
          </div>
          
          <p className="text-slate-700 mb-6">The CPM framework is intended to:</p>
          
          <div className="grid gap-3">
            {purposes.map((purpose, index) => (
              <div 
                key={index} 
                className="flex items-start gap-3 p-4 bg-slate-50 rounded-sm"
              >
                <CheckCircle className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">{purpose}</span>
              </div>
            ))}
          </div>
          
          <Card className="mt-6 border-slate-200 bg-slate-50">
            <CardContent className="p-4">
              <p className="text-sm text-slate-600 italic">
                The Matrix is a developmental model for structuring education and assessment systems. 
                It is not an educational product.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      <Separator className="max-w-4xl mx-auto" />

      {/* Seven Levels Section */}
      <section className="py-12">
        <div className="imcac-container max-w-4xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-sm bg-[#1B2A4A] flex items-center justify-center">
              <Award className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-[#1B2A4A] font-heading">The Seven Levels of Professional Progression</h2>
          </div>
          
          <p className="text-slate-700 mb-2">
            The following levels represent structural developmental stages within the framework.
          </p>
          <p className="text-slate-600 text-sm mb-6 font-medium">
            They are reference categories, not awarded titles.
          </p>
          
          <div className="space-y-3">
            {cpmLevels.map((level) => {
              const IconComponent = level.icon;
              return (
                <Card key={level.level} className="border-l-4 border-l-[#C5A572] hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-sm bg-[#1B2A4A] flex items-center justify-center">
                        <span className="text-white font-bold text-lg">{level.level}</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-[#1B2A4A]">Level {level.level} – {level.title}</h3>
                          <IconComponent className="h-4 w-4 text-[#C5A572]" />
                        </div>
                        <p className="text-sm text-[#C5A572] font-medium mb-2">{level.focus}</p>
                        <p className="text-sm text-slate-600">{level.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <Separator className="max-w-4xl mx-auto" />

      {/* Legal & Structural Clarification */}
      <section className="py-12">
        <div className="imcac-container max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-sm bg-[#1B2A4A] flex items-center justify-center">
              <Scale className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-[#1B2A4A] font-heading">Legal & Structural Clarification</h2>
          </div>
          
          <Card className="bg-amber-50 border-amber-200 mb-6">
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-[#1B2A4A] mb-3">The Clinical Progression Matrix:</h3>
                  <ul className="space-y-2">
                    {legalClarifications.map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-slate-700">
                        <span className="text-amber-600 font-bold">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              Recognition of progression may only occur when the framework is formally integrated into 
              structured educational or examination systems administered by authorized institutions or 
              governing bodies.
            </p>
            <p>
              Any credential, title, or certification must be awarded through separate, formally governed 
              examination and regulatory processes.
            </p>
          </div>
        </div>
      </section>

      <Separator className="max-w-4xl mx-auto" />

      {/* Governance & Cross-Specialty Applicability */}
      <section className="py-12">
        <div className="imcac-container max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-sm bg-[#1B2A4A] flex items-center justify-center">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-[#1B2A4A] font-heading">Governance & Cross-Specialty Applicability</h2>
          </div>
          
          <p className="text-slate-700 mb-4">The CPM is designed to be adaptable across:</p>
          
          <div className="grid sm:grid-cols-2 gap-3 mb-6">
            {applicabilityAreas.map((area, index) => (
              <div 
                key={index} 
                className="flex items-center gap-3 p-3 bg-slate-50 rounded-sm"
              >
                <CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0" />
                <span className="text-slate-700 text-sm">{area}</span>
              </div>
            ))}
          </div>
          
          <p className="text-slate-700">
            It provides structural guidance for competency progression while remaining discipline-neutral.
          </p>
        </div>
      </section>

      {/* Closing Section */}
      <section className="py-12 bg-[#1B2A4A]">
        <div className="imcac-container max-w-4xl text-center">
          <Shield className="h-10 w-10 text-[#C5A572] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white font-heading mb-3">
            Commitment to Responsible Competency Architecture
          </h2>
          <p className="text-slate-300 mb-4 max-w-3xl mx-auto">
            The Clinical Progression Matrix reinforces that advancement in medicine must be structured, 
            measurable, ethically grounded, and safety-aligned.
          </p>
          <p className="text-slate-300 mb-6 max-w-3xl mx-auto">
            It exists to guide institutions and governing authorities in designing responsible education 
            and examination ecosystems across all medical disciplines.
          </p>
          
          <div className="flex flex-wrap justify-center gap-3">
            <Button 
              className="bg-white text-[#1B2A4A] hover:bg-slate-100 rounded-sm"
              onClick={() => window.location.href = '/faculty/aesthetic-medicine'}
              data-testid="learn-cbeam-btn"
            >
              Learn About CBEAM <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button 
              variant="outline" 
              className="border-white text-white hover:bg-white/10 rounded-sm"
              onClick={() => window.location.href = '/contact'}
              data-testid="contact-btn"
            >
              Contact Academic Affairs
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
